import type { FC } from 'react';

import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  CHROME_QUIET_SCROLL,
  menuItems,
  type HeaderProps,
} from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/**
 * Voltic's category bar: the row a marketplace navigates from, set small and
 * tight so eight shelves fit on it without wrapping.
 */
const VOLTIC_NAV = cn(
  'flex h-11 items-center gap-6 overflow-x-auto whitespace-nowrap text-[12.5px] font-medium',
  CHROME_QUIET_SCROLL,
);

/**
 * A marketplace masthead, in three decks.
 *
 * The utility rail carries the shop's standing promises; the masthead carries
 * the wordmark and the shopper's own links; the bar beneath carries the
 * catalogue. Splitting them is what lets this family navigate from the header
 * at all - the other families put a handful of links beside a wordmark, and a
 * shop with nine shelves needs a row of its own for them.
 *
 * Like Serra, the bar falls back to the shop's own top-level shelves when the
 * merchant has built no menu: a marketplace header with nothing in it is not a
 * quieter marketplace, it is a shop with no way into the catalogue.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);
  // With no menu built the bar stands the shelves in, and the shelves query is
  // skipped in preview - so in preview an empty menu means no bar at all.
  const hasBar = items.length > 0 || !preview;

  return (
    <header
      className={cn(header.sticky && !preview && 'sticky top-0 z-40')}
      style={{ backgroundColor: 'var(--storefront-background)' }}
    >
      {/* Drawn around a rail like Serra's, and for the same reason: shipping
          thresholds and returns windows are what a marketplace leads with. */}
      <HeaderBannerRail header={header} fallback={description} preview={preview} />
      <div className="border-b" style={{ borderColor: 'var(--storefront-surface)' }}>
        <StorefrontContainer className="flex min-h-[4.5rem] items-center gap-3 py-3 sm:gap-6 preview-phone:gap-3!">
          <MobileNav menu={menu} hiddenFrom="md:hidden" preview={preview} categoryFallback />
          <StoreLogo
            storeName={storeName}
            logoUrl={logoUrl}
            logoWidthPx={header.logo_width_px}
            showStoreName={header.show_store_name}
            preview={preview}
            className="gap-2.5 sm:gap-3"
            nameClassName="text-xl font-bold tracking-[-0.02em] sm:text-[1.65rem] preview-phone:text-xl!"
            nameStyle={{ fontFamily: 'var(--storefront-heading-font)' }}
            /* The family's signature: the wordmark closed by a full stop in
               the accent, which is the one saturated mark in the chrome. */
            nameSuffix={<span style={{ color: 'var(--storefront-accent)' }}>.</span>}
          />
          <div className="ms-auto flex shrink-0 items-center gap-4 sm:gap-5 preview-phone:gap-4!">
            {endSlot}
          </div>
        </StorefrontContainer>
      </div>
      {hasBar ? (
        <div className="hidden border-b md:block preview-phone:hidden!"style={{ borderColor: 'var(--storefront-surface)' }}>
          <StorefrontContainer>
            {items.length > 0 ? (
              <nav aria-label={menu?.name} className={VOLTIC_NAV}>
                {items.map((item) => (
                  <MenuLink
                    key={`${item.label}:${item.url}`}
                    item={item}
                    preview={preview}
                    className="hover:opacity-60"
                  />
                ))}
              </nav>
            ) : (
              /* Six rather than the masthead default of four: this is a bar of
                 its own with a whole page column to spend. */
              <HeaderCategoryLinks
                className={VOLTIC_NAV}
                linkClassName="hover:opacity-60"
                header={header}
                limit={6}
              />
            )}
          </StorefrontContainer>
        </div>
      ) : null}
    </header>
  );
};
